/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */


import {Log} from "./Log.js";
/**
 * Utility helpers used across the client library.
 */
export class Utils {

    /**
     * Generates a new UUID v4.
     * Follows the RFC 4122 standard.
     *
     * Notes:
     *  - Prefer crypto.randomUUID() when available.
     *  - Fall back to crypto.getRandomValues() if randomUUID is unavailable.
     *  - Fall back to Math.random() as a last resort (kept for compatibility).
     *
     * @returns {string} A new UUID v4.
     *
     * @example
     * const uuid = Utils.v4UUID();
     * console.log(uuid);
     */
    static v4UUID() {
        try {
            const c = globalThis.crypto;
            if (c && typeof c.randomUUID === 'function') {
                return c.randomUUID();
            }
        } catch {
            // ignore
        }

        try {
            const c = globalThis.crypto;
            if (c && typeof c.getRandomValues === 'function') {
                const bytes = new Uint8Array(16);
                c.getRandomValues(bytes);

                // RFC 4122: set version and variant bits.
                bytes[6] = (bytes[6] & 0x0f) | 0x40;
                bytes[8] = (bytes[8] & 0x3f) | 0x80;

                const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0"));
                return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
            }
        } catch {
            // ignore
        }

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0,
                v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    //////////////////////////
    /// Encoding facilities //
    //////////////////////////x

    /**
     * URL-safe base64 helper used for embedding structured metadata into protocol fields.
     * @param {string} str
     * @returns {string}
     */
    static lBtoa(str) {
        try {
            if (typeof btoa === 'function') {
                return btoa(encodeURIComponent(str));
            } else {
                let buffer;
                if (str instanceof Buffer) {
                    buffer = str;
                } else {
                    buffer = Buffer.from(encodeURIComponent(str.toString()), 'binary');
                }
                return buffer.toString('base64');
            }
        } catch (e) {
            Log.error('[Kokabo.Utils.lBtoa] str ', str, 'error:', e.message)
            return str
        }
    }

    /**
     * Inverse of lBtoa; kept as a pair to ensure metadata round-trips across browser/Node runtimes.
     * @param {string} str
     * @returns {string}
     */
    static lAtob(str) {
        try {
            if (typeof atob === 'function') {
                return decodeURIComponent(atob(str))
            } else {
                return decodeURIComponent(Buffer.from(str, 'base64').toString('binary'));
            }
        } catch (e) {
            Log.error('[Kokabo.Utils.lAtob] str ', str, 'error:', e.message)
            return str
        }
    }

    /**
     * @param message
     * @return {Promise<string>}
     */
    static async sha256(message) {
        const msgBuffer = new TextEncoder().encode(message);

        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

        const hashArray = Array.from(new Uint8Array(hashBuffer));

        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }


    // Equivalent to Go's hex.EncodeToString; kept for cross-language protocol compatibility.
    static hexEncodeToString(str) {
        return Array.from(str, char => char.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    }

    // Equivalent to Go's hex.DecodeString; throws early to keep invalid wire data from silently corrupting state.
    static hexDecodeFromString(hex) {
        if (typeof hex !== 'string') {
            throw new Error('Input must be a string');
        }
        if (hex.length % 2 !== 0) {
            throw new Error('Hexadecimal string must have an even length');
        }
        let bytes = [];
        for (let i = 0; i < hex.length; i += 2) {
            let byte = parseInt(hex.slice(i, i + 2), 16);
            if (isNaN(byte)) {
                throw new Error('Invalid character found in hexadecimal string');
            }
            bytes.push(byte);
        }
        return bytes;
    }


    /**
     * Waits until a URL becomes reachable.
     * Minimum per-try delay avoids busy-looping when callers provide very small timeouts.
     * @param {string} url
     * @param {number} maxRetries
     * @param {number} timeout
     **/
    static async waitForReachabilityOf(url, maxRetries, timeout) {
        if (typeof url !== 'string' || !url) {
            throw new Error('Invalid URL');
        }
        if (typeof maxRetries !== 'number' || maxRetries <= 0) {
            maxRetries = 1;
        }
        if (typeof timeout !== 'number' || timeout <= 0) {
            throw new Error('Invalid timeout');
        }
        let divisor = maxRetries > 0 ? maxRetries : 1;
        let sleepDelay = timeout / divisor;
        if (sleepDelay < 20) {
            sleepDelay = 20;
        }

        for (let i = 0; i < maxRetries; i++) {
            try {
                const controller = new AbortController();
                const signal = controller.signal;
                const fetchTimeout = setTimeout(() => controller.abort(), sleepDelay);
                const response = await fetch(url, {signal});
                clearTimeout(fetchTimeout);
                if (response.ok) {
                    return;
                }
            } catch (error) {
                if (Log.supports(Log.Core)) {
                    Log.core(`[Kokabo.Utils.waitForReachabilityOf] Error Attempt ${i + 1} failed for URL: ${url} ${error.message}`);
                }
            }
            await Utils.sleep(sleepDelay);
        }
        throw new Error(`timeout: ${url} is not reachable`);
    }

    /**
     * @param ms
     * @return {Promise<unknown>}
     */
    static async sleep(ms) {
        if (ms <= 0) {
            return Promise.resolve();
        } else {
            return new Promise(resolve => setTimeout(resolve, ms));
        }
    }

    static LowerAlphaCharset = "abcdefghijklmnopqrstuvwxyz";
    static UpperAlphaCharset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    static AlphaNumericCharset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    static AlphaNumericLowerCharset = "abcdefghijklmnopqrstuvwxyz0123456789";
    static NumericCharset = "0123456789";
    static HexCharset = "0123456789abcdef";
    static ZzzzCharset = "Zzzz";

    /**
     * @param {number} length
     * @param {string} charset
     * @returns {string}
     */
    static randomStringInCharset(length, charset) {
        let result = '';
        const charsetLength = charset.length;
        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * charsetLength);
            result += charset[randomIndex];
        }
        return result;
    }

    /**
     * @param {Uint8Array} bytes
     * @return {string}
     */
    static bytesToBase64(bytes) {
        return btoa(String.fromCharCode.apply(null, bytes));
    }

    /**
     * @param {string} base64
     * @return {Uint8Array}
     */
    static base64ToBytes(base64) {
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    }

}